import React, { useEffect, useRef, useState } from 'react';
import HeaderSearch from 'components/home-page/header-search';
import Link from 'components/link';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import useQueryParams from 'hooks/use-query-params';
import { useModalContext } from 'contexts/modal';
import Button from 'components/control/button';
import { buildClassName } from 'utils/build-class-name';
import { trackEvent } from 'utils/google-tag-manager';
import styles from '../style.module.scss';
import DEFAULT_LISTING_PARAMS from 'contexts/preferences/listing-params/defaults';

import { useUserContext, usePreferencesContext } from 'contexts';
import { CountryCodeList } from 'types/countries';
import { homepageIds } from 'constants/test-constants';
import { generateSlugAndCoordinatesFromLastSearch, LastSearchSlugAndCoordsResponse } from 'data/search-predictions';
import { GTM_CLICK_HOMEPAGE_SOLD_BUTTON,
  GTM_CLICK_HOMEPAGE_FEATURED_BUTTON, GTM_CLICK_HOMEPAGE_BUYERS_CARD_CLICK, GTM_CLICK_HOMEPAGE_BUYERS_BUTTON_CLICK,
  GTM_CLICK_HOMEPAGE_SELLERS_CARD_CLICK, GTM_CLICK_HOMEPAGE_SELLERS_BUTTON_CLICK } from 'constants/events';
import { ModalOpeners } from 'types/modal';
import replaceFrenchAccents from 'utils/replace-french-accents';
import dynamic from 'next/dynamic';
import { useOnListingCardClick } from 'utils/listing-card';

import ZoocasaForSaleSection from '../themed-for-sale-section/zoocasa';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import InternalLinksContainer from '../internal-links/internalLinksContainer';
import { useIsTablet } from 'hooks/use-size-class';

import type { HomePageProps } from '../index';
import { getTenantById } from '@tenants';
import ContentBlock from '../content-block';
import ListingCardCarousel from 'components/listing-card-carousel';
import LoadingText from 'components/loading-text';
import { CTA_CARD_TYPE_FEATURED, CTA_CARD_TYPE_SOLD } from '../home-modules/cta-card';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_VIEW_HOME_PAGE } from 'constants/events';

// Dynamic imports for conditionally loaded components
const ZoocasaCanadaBlogSection = dynamic(() => import('./zoocasa-canada-blog-section'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const KeywordsList = dynamic(() => import('../keywords-list'), {
  loading: () => <LoadingText />,
  ssr: true,
});

const Maintenance = dynamic(() => import('components/maintenance'), {
  loading: () => <></>,
  ssr: false,
});

const NotificationBanner = dynamic(() => import('components/notification-banner'), {
  loading: () => <></>,
  ssr: false,
});


export default function HomePageZoocasa({ isCrawler, isNotificationBannerHidden, availableHomeListings, availableHomeListingsMeta, availableHomeListingsLocation, isExpandedArea, internalLinksData, maintenance, routeName, headerData, tenantId, soldHomeListings, featuredHomeListings, homePageUrls }: HomePageProps) {
  const tenant = getTenantById(tenantId);
  const theme = tenant.theme;
  const { userLocation, siteLocation, setIsOnSignUpUrl, isAuthenticated } = useUserContext();
  const { lastSearchLocation } = usePreferencesContext();
  const { openModal } = useModalContext();
  const pageRef = useRef(null);

  const [currentSlug, setCurrentSlug] = useState(DEFAULT_LISTING_PARAMS.filter.slug);
  const [currentAreaName, setCurrentAreaName] = useState(DEFAULT_LISTING_PARAMS.filter.areaName);
  const [currentLatitude, setCurrentLatitude] = useState(DEFAULT_LISTING_PARAMS.filter.latitude);
  const [currentLongitude, setCurrentLongitude] = useState(DEFAULT_LISTING_PARAMS.filter.longitude);
  const [currentSublocation, setCurrentSublocation] = useState('');
  const isCanadianSite = siteLocation === CountryCodeList.CANADA;
  const isTablet = useIsTablet();
  const onListingCardClick = useOnListingCardClick({ isHomePage: true });
  const { homesForSaleUrl, mapViewUrl, soldPricesUrl } = homePageUrls;

  const generateCoordinatesAndSlug = async () => {
    if (lastSearchLocation) {
      generateSlugAndCoordinatesFromLastSearch(lastSearchLocation).then((response: LastSearchSlugAndCoordsResponse) => {
        setCurrentSlug(response?.slug || currentSlug);
        setCurrentAreaName(lastSearchLocation.description);
        setCurrentLatitude(response?.latitude || currentLatitude);
        setCurrentLongitude(response?.longitude || currentLongitude);
        setCurrentSublocation(response?.subLocation);
      });
    }
    else if (userLocation && userLocation.countryCode === 'US') {
      setCurrentSlug(userLocation.slug);
      setCurrentAreaName(userLocation.name);
      setCurrentLatitude(userLocation.latitude);
      setCurrentLongitude(userLocation.longitude);
      setCurrentSublocation('');
    }
    else {
      setCurrentSlug(isCanadianSite ? defaultCACityPayload.slug : defaultUSCityPayload.slug);
      setCurrentAreaName(isCanadianSite ? defaultCACityPayload.name : defaultUSCityPayload.name);
      setCurrentLatitude(isCanadianSite ? defaultCACityPayload.latitude : defaultUSCityPayload.latitude);
      setCurrentLongitude(isCanadianSite ? defaultCACityPayload.longitude : defaultUSCityPayload.longitude);
      setCurrentSublocation('');
    }
  };

  useQueryParams(params => openModal('reset-password', params), ModalOpeners.RESET_PASSWORD_TOKEN, ModalOpeners.EMAIL);
  useQueryParams(params => openModal('confirm-account', params), ModalOpeners.CONFIRMATION_TOKEN, ModalOpeners.USER_AGENT);
  useQueryParams(params => openModal('login-registration', params), ModalOpeners.SIGNIN_EMAIL);
  useQueryParams(params => openModal('login-registration', params), ModalOpeners.WARNING_MESSAGE);
  useQueryParams(params => openModal('notification', { message: params.notification as string }), ModalOpeners.NOTIFICATION);

  useEffect(() => {
    const isLoginUrl = window.location.href.endsWith('#sign-up');
    if (isLoginUrl && !isAuthenticated) {
      setIsOnSignUpUrl(true);
      openModal('login-registration');
      // Trigger a click event on the screen otherwise modal will not open
      setTimeout(() => {
        pageRef.current?.click();
      }, 0);
    }
    else {
      setIsOnSignUpUrl(false);
    }

    generateCoordinatesAndSlug();
    AnalyticsController.trackEvent(GTM_VIEW_HOME_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    generateCoordinatesAndSlug();
  }, [lastSearchLocation, homesForSaleUrl, mapViewUrl]);


  return (
    <>
      <HeadData data={headerData} />
      <Layout routeName={routeName} showMobileBottomNav={false}>
        <NotificationBanner label="New Construction Homes are here!" link="/newhomes" isHidden={isNotificationBannerHidden} />
        <HeaderSearch />
        <section className={styles.component} ref={pageRef}>
          <div className={buildClassName(styles['home-modules'], styles.top)}>
            <ZoocasaForSaleSection isCrawler={isCrawler} availableHomeListings={availableHomeListings} availableHomeListingsMeta={availableHomeListingsMeta} availableHomeListingsLocation={availableHomeListingsLocation} isExpandedArea={isExpandedArea} homesForSaleUrl={homesForSaleUrl} mapViewUrl={mapViewUrl} onListingCardClick={onListingCardClick} />
            {isCanadianSite && <ZoocasaCanadaBlogSection />}
            <section className={buildClassName(styles['home-sections'], styles.sold)}>
              <div className={styles['home-block']} data-testid={homepageIds.soldPricesWidget}>
                <div className={styles.row}>
                  <Link href={replaceFrenchAccents(soldPricesUrl)} testId={homepageIds.soldPricesTitle} onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)}>
                    <h2>Sold Prices</h2>
                    <p className={styles.label}>Search Sold Data from 2003 - {new Date().getFullYear()}</p>
                  </Link>
                  { !isTablet &&
                        <Link href={replaceFrenchAccents(soldPricesUrl)}>
                          <Button label="View Sold Properties" theme="secondary" expHomePadding={false} onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)} testId={homepageIds.soldPricesMoreDataButton} />
                        </Link>
                  }
                </div>
                <ListingCardCarousel listingCardsData={{ listings: soldHomeListings, ctaType: CTA_CARD_TYPE_SOLD, userLocation, redirectUrl: replaceFrenchAccents(soldPricesUrl) }} isCrawler={isCrawler} onListingCardClick={onListingCardClick}/>
                { isTablet &&
                      <Link href={replaceFrenchAccents(soldPricesUrl)}>
                        <Button className={styles['tablet-button']} label="View Sold Properties" theme="secondary" expHomePadding={false} onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_SOLD_BUTTON)} testId={homepageIds.soldPricesMoreDataButton} />
                      </Link>
                }
              </div>
            </section>
          </div>
          <div className={buildClassName(styles['home-modules'], styles.bottom)}>
            {isCanadianSite &&
              <section className={buildClassName(styles['home-sections'], styles['featured-homes'])}>
                <div className={styles['home-block']} data-testid={homepageIds.featuredHomesWidget}>
                  <div className={styles.row}>
                    <Link href="/ca-real-estate/houses" onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}>
                      <h2 data-testid={homepageIds.featuredHomesTitle}>Featured Homes</h2>
                    </Link>
                    { !isTablet &&
                          <Link href="/ca-real-estate/houses">
                            <Button label="View More" theme="secondary" testId={homepageIds.featuredHomesMoreListingsButton} onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}/>
                          </Link>
                    }
                  </div>
                  <ListingCardCarousel listingCardsData={{ listings: featuredHomeListings, ctaType: CTA_CARD_TYPE_FEATURED, userLocation }} isCrawler={isCrawler} onListingCardClick={onListingCardClick} />
                  { isTablet &&
                        <Link href="/ca-real-estate/houses">
                          <Button className={styles['tablet-button']} label="View More" theme="secondary" testId={homepageIds.featuredHomesMoreListingsButton} onClick={() => trackEvent(GTM_CLICK_HOMEPAGE_FEATURED_BUTTON)}/>
                        </Link>
                  }
                </div>
              </section>
            }
            <div>
              <section className={buildClassName(styles['home-sections'], styles['content-block'], styles['buy-guide'])}>
                <div className={styles['home-block']}>
                  <ContentBlock content={theme.contentBlockBuy} cardEvent={GTM_CLICK_HOMEPAGE_BUYERS_CARD_CLICK} buttonEvent={GTM_CLICK_HOMEPAGE_BUYERS_BUTTON_CLICK} />
                </div>
              </section>
              <section className={buildClassName(styles['home-sections'], styles['content-block'], styles['buy-guide'])}>
                <div className={styles['home-block']}>
                  <ContentBlock content={theme.contentBlockSell} cardEvent={GTM_CLICK_HOMEPAGE_SELLERS_CARD_CLICK} buttonEvent={GTM_CLICK_HOMEPAGE_SELLERS_BUTTON_CLICK} right={true} />
                </div>
              </section>
            </div>
          </div>
          {isCanadianSite &&
            <section className={buildClassName(styles['home-sections'], styles['find-by-keyword'])} data-testid={homepageIds.searchByKeywordsWidget}>
              <div className={styles.container}>
                <div className={styles['home-block']}>
                  <div className={buildClassName(styles.row, styles['find-by-keyword-heading'])} data-testid={homepageIds.searchByKeywordsTitle}>
                    <h2>Search by Keywords</h2>
                  </div>
                  <div>
                    <KeywordsList slug={currentSlug} areaName={currentAreaName} latitude={currentLatitude} longitude={currentLongitude} subLocation={currentSublocation} />
                  </div>
                </div>
              </div>
            </section>
          }
        </section>
        <InternalLinksContainer data={internalLinksData} />
        {maintenance.isMaintenance && <Maintenance maintenance={maintenance} />}
      </Layout>
    </>
  );
}